import { useMemo } from 'react'
import type { CurrencyCode } from '@klear/constants/currency'
import { Money } from '@klear/money'
import { usePayables } from '@klear/ui/hooks/use-payables'
import toNumber from 'lodash/toNumber'
import toPairs from 'lodash/toPairs'
import { paymentIsDraft } from '@klear/payment/status'

export type SupplierGrowthItem = {
  name: string
  growthRate: number
}

type MonthSupplierEntry = {
  monthKey: string
  supplierName: string
  value: Money | null
}

export function useSupplierGrowthData(selectedCurrency: CurrencyCode, traderId: number) {
  const { payables, isLoading, error } = usePayables(traderId)

  const validPayables = useMemo(() => {
    return payables.filter((payable) => {
      if (paymentIsDraft(payable.status)) return false
      if (!payable.supplierName) return false
      const money = payable.amount
      return money != null && money.currency === selectedCurrency && money.isPositive()
    })
  }, [payables, selectedCurrency])

  const monthsData = useMemo(() => {
    if (!validPayables.length) return []

    const monthSupplierMap = new Map<string, MonthSupplierEntry>()

    validPayables.forEach((payable) => {
      const supplierName = payable.supplierName
      if (!supplierName) return

      const monthKey = payable.effectiveDate.startOf('month').toJSON()
      const key = `${monthKey}-${supplierName}`

      if (!monthSupplierMap.has(key)) {
        monthSupplierMap.set(key, { monthKey, supplierName, value: null })
      }

      const entry = monthSupplierMap.get(key)!
      entry.value = entry.value ? entry.value.add(payable.amount) : payable.amount
    })

    const byMonth = new Map<string, Array<MonthSupplierEntry & { value: Money }>>()
    monthSupplierMap.forEach((entry) => {
      if (entry.value == null) return
      if (!byMonth.has(entry.monthKey)) byMonth.set(entry.monthKey, [])
      byMonth.get(entry.monthKey)!.push({ ...entry, value: entry.value })
    })

    return [...byMonth.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([, entries]) => entries)
  }, [validPayables])

  const { growingSuppliers, decliningSuppliers } = useMemo(() => {
    if (monthsData.length < 2) {
      return { growingSuppliers: [], decliningSuppliers: [] }
    }

    const recentMonths = monthsData.slice(-3)
    const priorMonths = monthsData.slice(-6, -3)

    if (!priorMonths.length) {
      return { growingSuppliers: [], decliningSuppliers: [] }
    }

    const supplierVolume: Record<string, { recent: number; prior: number }> = {}

    recentMonths.forEach((month) => {
      month.forEach(({ supplierName, value }) => {
        if (!supplierVolume[supplierName]) supplierVolume[supplierName] = { recent: 0, prior: 0 }
        const minor = toNumber(value.amount.toString())
        supplierVolume[supplierName]!.recent += minor
      })
    })

    priorMonths.forEach((month) => {
      month.forEach(({ supplierName, value }) => {
        if (!supplierVolume[supplierName]) supplierVolume[supplierName] = { recent: 0, prior: 0 }
        const minor = toNumber(value.amount.toString())
        supplierVolume[supplierName]!.prior += minor
      })
    })

    const rates = toPairs(supplierVolume)
      .filter(([, data]) => data.prior > 0 && data.recent > 0)
      .map(([name, data]) => ({
        name,
        growthRate: ((data.recent - data.prior) / data.prior) * 100
      }))

    const growingSuppliers: SupplierGrowthItem[] = rates
      .filter((s) => s.growthRate > 0)
      .sort((a, b) => b.growthRate - a.growthRate)
      .slice(0, 5)

    const decliningSuppliers: SupplierGrowthItem[] = rates
      .filter((s) => s.growthRate < 0)
      .sort((a, b) => a.growthRate - b.growthRate)
      .slice(0, 5)

    return { growingSuppliers, decliningSuppliers }
  }, [monthsData])

  return { growingSuppliers, decliningSuppliers, isLoading, error }
}
